import { CacheKeys, ServerActionName } from '@headless-workspace/config';
import {
    AsyncData,
    ContentAssetKeys,
    ResultEmpty,
    ResultFailure,
    ResultSuccess,
    unwrapResultSuccess,
} from '@headless-workspace/core-domain';
import { useClientData, useSafeSWR } from '../../../../../client';
import { ContentAssetType, getContentAssetServerAction } from '../../../../../server';

const PRICE_LEGAL_MENTIONS_ASSET_NOT_FOUND_ERROR_KEY = 'PRICE_LEGAL_MENTIONS_ASSET_NOT_FOUND_ERROR';
const PRICE_LEGAL_MENTIONS_ASSET_MESSAGE_ERROR =
    'An unexpected error occurred while fetching the price legal mentions.';

export const usePriceRegulationAssets = (): AsyncData<ContentAssetType> => {
    const { getContentAssets } = useClientData();
    const contentAsset = getContentAssets(ContentAssetKeys.PriceRegulationLegalMentions);

    const {
        data: swrData,
        isLoading,
        error,
    } = useSafeSWR(
        contentAsset ? null : CacheKeys.PriceRegulationAsset,
        ServerActionName.getContentAssetServerAction,
        () => getContentAssetServerAction([ContentAssetKeys.PriceRegulationLegalMentions]),
        { fallbackData: contentAsset },
    );

    if (contentAsset) {
        return {
            isLoading,
            result: ResultSuccess(contentAsset),
        };
    }

    if (swrData?.type !== 'success' || swrData.data.length === 0) {
        if (error) {
            return {
                isLoading,
                result: ResultFailure(
                    PRICE_LEGAL_MENTIONS_ASSET_NOT_FOUND_ERROR_KEY,
                    error instanceof Error ? error.message : PRICE_LEGAL_MENTIONS_ASSET_MESSAGE_ERROR,
                ),
            };
        }

        return {
            isLoading,
            result: ResultEmpty(),
        };
    }

    const asset = unwrapResultSuccess(swrData);
    const legalMentionAsset = asset?.find((item) => item.id === ContentAssetKeys.PriceRegulationLegalMentions);

    return {
        isLoading,
        result: legalMentionAsset?.content?.html?.content ? ResultSuccess(legalMentionAsset) : ResultEmpty(),
    };
};
